import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Directory from './pages/Directory';
import Pricing from './pages/Pricing';
import Plans from './pages/Plans';
import Tiers from './pages/Tiers';
import TierDetail from './pages/TierDetail';
import Support from './pages/Support';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Tos from './pages/Tos';
import Privacy from './pages/Privacy';
import { recordHostingDocumentVisit } from './api/client';
import { VISIT_KEY_FAIRCORP_HOSTING } from './constants/visitKeys';
import { BRAND_NAME } from './lib/site';

const PAGE_TITLES: Record<string, string> = {
  '/': BRAND_NAME,
  '/pricing': 'Development Pricing',
  '/plans': 'Hosting Plans',
  '/tiers': 'Tier Reference',
  '/sites': 'Hosted Sites',
  '/support': 'Support',
  '/contact': 'Contact',
  '/tos': 'Terms of Service',
  '/privacy': 'Privacy Policy',
};

const App: React.FC = () => {
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // Every document load (direct URL, deep link, hard refresh). Soft in-app routes do not remount App.
  useEffect(() => {
    recordHostingDocumentVisit(VISIT_KEY_FAIRCORP_HOSTING);
  }, []);

  const location = useLocation();

  // SPA navigation: new title, and reset scroll (or jump to #hash) like a real page load.
  useEffect(() => {
    const tierMatch = location.pathname.match(/^\/tiers\/(t[1-5][ab]?)$/i);
    const page = tierMatch ? `${tierMatch[1].toUpperCase()} Tier` : PAGE_TITLES[location.pathname];
    document.title = !page ? `Page not found · ${BRAND_NAME}` : location.pathname === '/' ? page : `${page} · ${BRAND_NAME}`;
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView();
        return;
      }
    }
    if (typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom: not implemented */
      }
    }
  }, [location.pathname, location.hash]);

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <MotionConfig reducedMotion={prefersReducedMotion ? 'always' : 'never'}>
      <div
        className="flex flex-col min-h-screen min-h-[100dvh] bg-secondary-950 text-white overflow-x-clip"
        style={{ fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}
      >
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-3 focus:rounded-lg focus:bg-primary-600 focus:text-white"
        >
          Skip to content
        </a>
        <Navbar />
        <div
          className="shrink-0"
          style={{ height: 'var(--header-height, 3.5rem)' }}
          aria-hidden="true"
        />
        <main id="main" className="flex-1">
          <motion.div key={location.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/sites" element={<Directory />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/tiers" element={<Tiers />} />
              <Route path="/tiers/:tier" element={<TierDetail />} />
              <Route path="/support" element={<Support />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/tos" element={<Tos />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </motion.div>
        </main>
        <Footer />
      </div>
    </MotionConfig>
  );
};

export default App;
